import { batch, SignalObject, signalObject } from "omi";

import type { ToolbarView } from "@/pages/project/features/toolbar/views";
import { FeatureSlice } from "@/store/features";
import { getFromQueryString } from "@/utils/url";
import type { RouteName } from "@/router";
import { Bootstrap } from "@/store";

type ToggledBy = "hover" | "toolbar" | "share-modal" | "close-btn" | "mobile-first-time" | "onboarding" | undefined;

export type AppSettings = {
  appVersion: string | undefined;
};

type Slice = {
  isImpersonating: boolean;
  isNavOpen: boolean;
  toggledBy: ToggledBy;
  activeToolbarView: ToolbarView | undefined;
  route: RouteName;
} & AppSettings;

type Actions = {
  toggleNav: (open: boolean, source: ToggledBy | undefined) => void;
  showToolbarView: (view: ToolbarView | undefined) => void;
  closeToolbar: () => void;
  isRouteActive: (path: RouteName) => boolean;
  setRoute: (path: RouteName) => void;
};

export type AppSlice = SignalObject<Slice> & Actions;

export function createAppSlice(appSettingsBootstrap: Bootstrap["appSettings"], featureSlice: FeatureSlice): AppSlice {
  const initialState: Slice = {
    isNavOpen: false,
    toggledBy: undefined,
    activeToolbarView: undefined,
    isImpersonating: false,
    appVersion: appSettingsBootstrap?.appVersion,
    route: getInitialRoute(),
  };

  const state = signalObject(initialState);

  return { ...state, ...actions(state, featureSlice) };
}

function getInitialRoute(): RouteName {
  if (isRootPath()) {
    return "onboarding";
  }

  const isPortfolio = getFromQueryString("portfolio", false) === "true";
  if (isPortfolio) {
    return "portfolio";
  }

  return "project";
}

function isRootPath() {
  try {
    const url = new URL(window.location.href);
    return url.pathname === "/" || url.pathname === "";
  } catch (error) {
    console.error(error);
    return false;
  }
}

function actions(state: SignalObject<Slice>, featureSlice: FeatureSlice): Actions {
  const toggleNav = (open: boolean, source?: ToggledBy) => {
    batch(() => {
      if (featureSlice.hasFeature("projects.navV2")) {
        showToolbarView(open ? "directory" : undefined);
        state.toggledBy.value = source;
      } else {
        state.toggledBy.value = source;
        state.isNavOpen.value = open;
      }
    });
  };

  const showToolbarView = (view: ToolbarView | undefined) => {
    if (state.activeToolbarView.value === view) {
      state.activeToolbarView.value = undefined;
      if (view === "directory") {
        state.isNavOpen.value = false;
      }
    } else {
      state.activeToolbarView.value = view;
      state.isNavOpen.value = view === "directory";
    }
  };

  const closeToolbar = () => {
    showToolbarView(undefined);
  };

  const isRouteActive = (path: RouteName) => {
    return state.route.value === path;
  };

  const setRoute = (path: RouteName) => {
    state.route.value = path;
  };

  return {
    toggleNav,
    showToolbarView,
    closeToolbar,
    isRouteActive,
    setRoute,
  };
}
